import { createContext, FC, MouseEvent, useEffect, useMemo } from 'react'
import arrowTriangle from '@assets/images/arrow-triangle.svg'
import settings from '@assets/images/setings.svg'
import { Button } from '@microservices/wiskey-react-components'
import { Box, Grid } from '@mui/material'

import { HandleSetOpenedContainersParams } from '@pages/ConfiguredEntity/types'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { CustomSvgIcon } from '@components/CustomSvgIcon'
import { MenuPortal } from '@components/MenuPortal'
import { RedirectToConfigButton } from '@components/RedirectToConfigButton'

import { updateLinkTitle } from '@redux/reducers/formHistory.reducer'

import { useAppDispatch, useContextMenu } from '@hooks'
import { buttonStylesByMode, getInitialPositionWindow } from '@helpers'
import {
  ENTITY,
  FLEXIBLE,
  FORM_TYPE,
  MENU_POINT_TYPES,
  OuterDataType,
  PreFillType,
  WINDOW_FORM_CONFIG_LINE_HEIGHT,
} from '@constants'

import {
  LinkType,
  LinkViewHistory,
  MenuPointOptionType,
  ObjectDataRecord,
  PassedFormData,
} from '../../types'
import { AppLoader } from '../AppLoader'

import { DisplayFormContent } from './components/DisplayFormContent'
import { BreadcrumbsForm } from './components'
import { getFormTitle } from './helpers'
import { useDisplayForm } from './hooks'
import { FormContextType, FormStateType } from './types'

type DisplayFormProps = {
  passedParams: PassedFormData
  onChangeDirty?: (value: boolean) => void
  onSetWindowTitle?: (title: string | null) => void
  onBreadcrumbsMainLinkClick: (link?: LinkViewHistory) => void
  onObjectDataRecordSuccessCreating?: (links: LinkType[], data: ObjectDataRecord) => void
  handleNavigateToDefaultPage: () => void
  handleChangeFormCode: (formCode: string, verifyFormCode: string) => void
  handleRemoveSamePageForm: (formDataId?: number, isDirty?: boolean) => void
  handleUpdateSamePageFormData: (
    oldObjectId: string,
    newFormCode: string,
    objectCode: string,
    objectId: string
  ) => void
  handleCloseCurrentFormTab: () => void
  entityType: ENTITY
  onSetOpenedContainersInStaticForms: (params: HandleSetOpenedContainersParams) => void
  samePageFormsData: PassedFormData[] | undefined
  selectedRowIds: string[] | undefined
  skipHotKeyPress?: boolean
  dialogId?: string
  isFormDialogWindow?: boolean
  windowHeight?: number
  windowWidth?: number
  preFill?: PreFillType
  dialogMeta?: any
  outerData?: OuterDataType
  resetFieldsAfterCreate?: boolean
  isSectionWindow?: boolean
}

export const FormContext = createContext<FormContextType>({} as FormContextType)
export const FormStateContext = createContext<FormStateType>({} as FormStateType)

export const DisplayForm: FC<DisplayFormProps> = ({ ...props }) => {
  const {
    passedParams,
    handleRemoveSamePageForm,
    onSetOpenedContainersInStaticForms,
    // TODO необходим рефакторинг, скорее всего оставить данную логику только для статичных форм и вынести наружу
    samePageFormsData,
    selectedRowIds,
    onChangeDirty,
    skipHotKeyPress,
    windowHeight,
    windowWidth,
    onSetWindowTitle,
    dialogId,
    isFormDialogWindow = false,
    isSectionWindow,
  } = props
  const dispatch = useAppDispatch()

  const { data, state, handlers } = useDisplayForm({
    ...props,
  })
  const { globalId, objectId } = passedParams

  const {
    form,
    links,
    viewCode,
    isFetchingForm,
    isFetchingFormError,
    view,
    withPassedParams,
    fetchedContainers,
    isViewType,
    viewPath,
    isDirty,
    methods,
    dirtyFields,
    formCodeToFetchBy,
    isValid,
    outerVariables,
  } = data

  const {
    currentRow,
    isViewEdit,
    defaultValues,
    isDeleteRow,
    isDropdownWindowOpenFormState,
    initialCommonParameters,
    isLoadingRow,
    currentLink,
    isSaveByEnterDisabled,
    isSuccessUpdateObjectData,
    isPendingCreateOrUpdateForm,
    latestVariables,
  } = state

  const {
    handleDataForNewForm,
    handleSetType,
    handleOpenLink,
    handleEditOrSaveClick,
    handleCancel,
    handleResetEdit,
    handleSetDropdownWindowOpenFormState,
    reset,
    setValueOnForm,
    watch,
    handleSetSaveByEnterDisabled,
    handleSetHasUnsavedChanges,
  } = handlers

  useEffect(() => {
    if (onChangeDirty) {
      onChangeDirty(isDirty)
    }
  }, [onChangeDirty, isDirty])

  const formTitle = useMemo(() => {
    if (currentLink.event === FORM_TYPE.CREATE) {
      if (form) {
        return getFormTitle(form, {})
      }
    } else {
      if (form && currentRow && !isLoadingRow && !isFetchingForm) {
        return getFormTitle(form, currentRow)
      }
    }

    return null
  }, [form, currentRow, isLoadingRow, isFetchingForm])

  useEffect(() => {
    if (!isSectionWindow) {
      onSetWindowTitle?.(formTitle)
      if (dialogId && formTitle) {
        dispatch(updateLinkTitle({ id: dialogId, title: formTitle }))
      }
    }
  }, [formTitle, isSectionWindow])

  const contextValue: FormContextType = {
    dialogId,
    isDropdownWindowOpenFormState,
    handleSetDropdownWindowOpenFormState,
    form,
    globalId,
    links,
    viewCode: viewCode ?? '',
    row: currentRow ?? {},
    type: currentLink.event,
    isLoadingRow,
    onSetNewForm: handleDataForNewForm,
    isFetchingForm,
    isPendingCreateOrUpdateForm,
    isFetchingFormError,
    onSetType: handleSetType,
    viewPath,
    auditFieldHistory: view?.auditFieldHistory,
    isSaveByEnterDisabled,
    handleSetSaveByEnterDisabled,
    withPassedParams,
    formDataId: passedParams.id,
    containers: fetchedContainers || [],
    objectCode: currentLink.objectCode,
    openLink: handleOpenLink,
    isViewType,
    isViewEdit,
    handleEditOrSaveClick,
    handleCancel,
    onResetEdit: handleResetEdit,
    methods,
    isDirty,
    reset,
    defaultValues,
    isDeleteRow,
    objectId,
    handleRemoveSamePageForm,
    onSetOpenedContainersInStaticForms,
    samePageFormsData,
    selectedRowIds,
    skipHotKeyPress,
    initialCommonParameters,
    windowHeight,
    formTitle,
    onSetWindowTitle,
    currentLink,
    setValueOnForm,
    watch,
    isSuccessUpdateObjectData,
    formCodeToFetchBy,
    isSectionWindow,
    handleSetHasUnsavedChanges,
    latestVariables,
    outerVariables,
  }

  const {
    onClickLink,
    onExecuteCommand,
    onOpenContextMenu,
    onCloseContextMenu,
    position,
    onExecuteAction,
  } = useContextMenu({ dialogId: globalId, variantPosition: 'element' })
  const menuIgnoreSelectorClassName = 'menuButton'

  const handleClickContextMenuItem = (data: {
    metaData: {
      row?: ObjectDataRecord
      menuItem: MenuPointOptionType
      event: MouseEvent<HTMLDivElement>
    }
  }) => {
    if (data.metaData.menuItem.menuPointType === MENU_POINT_TYPES.LINK) {
      const position = getInitialPositionWindow(data.metaData.event)
      onClickLink(data.metaData.menuItem, position)

      return
    }

    if (data.metaData?.menuItem.menuPointType === MENU_POINT_TYPES.COMMAND) {
      onExecuteCommand(data.metaData)

      return
    }

    if (data.metaData?.menuItem.menuPointType === MENU_POINT_TYPES.ACTION) {
      onExecuteAction(data.metaData)
    }
  }

  const handleClickButtonContextMenu = (event: MouseEvent<HTMLElement>) => {
    if (!position) {
      onOpenContextMenu(event)
    } else {
      onCloseContextMenu()
    }
  }

  return (
    <FormContext.Provider value={contextValue}>
      <FormStateContext.Provider value={{ isDirty, isViewType, dirtyFields, isFormDialogWindow }}>
        <Grid container justifyContent={'space-between'} px={1}>
          <Grid item>
            {props.dialogId && links && (
              <BreadcrumbsForm
                dialogId={props.dialogId}
                links={links}
                maxItems={4}
                openLink={handleOpenLink}
                windowWidth={windowWidth}
              />
            )}
          </Grid>
          <RedirectToConfigButton code={form?.code} sx={{ p: 0, mt: 1.25, mr: 0.5 }} type='form' />
          <Grid item pr={0.5}>
            {form?.contextMenu && (
              <MenuPortal
                items={form?.contextMenu?.menuPoints}
                position={position}
                selectorsIgnore={[menuIgnoreSelectorClassName]}
                dataTransfer={{
                  changes: {},
                  config: {},
                  data: {},
                  metaData: { srcObj: currentRow },
                }}
                onClose={onCloseContextMenu}
                onItemClick={handleClickContextMenuItem}
              />
            )}
            <Button
              className={menuIgnoreSelectorClassName}
              disabled={!form?.contextMenu}
              sx={theme => ({
                ...buttonStylesByMode(theme),
                p: '0 !important',
                height: 21,
                width: 54,
                minWidth: 54,
                justifyContent: 'space-between',
                pl: '10px',
                pr: '6px',
                mt: 1.5,
                ...(position && {
                  background: theme.palette.background.buttonHovered,
                  boxShadow: theme.palette.boxShadow.buttonHovered,
                }),
              })}
              onClick={handleClickButtonContextMenu}
            >
              <CustomSvgIcon
                className={menuIgnoreSelectorClassName}
                imgClassName={menuIgnoreSelectorClassName}
                imgStyle={{}}
                src={settings}
                sx={{ fontSize: '1rem', ml: '8px' }}
              />
              <CustomSvgIcon
                className={menuIgnoreSelectorClassName}
                imgClassName={menuIgnoreSelectorClassName}
                src={arrowTriangle}
                sx={{ fontSize: '1rem', mr: '6px' }}
              />
            </Button>
          </Grid>
        </Grid>
        {!withPassedParams ? (
          <PageContentLayout
            isBreadcrumbs
            height={isSectionWindow ? `calc(100% - ${WINDOW_FORM_CONFIG_LINE_HEIGHT}px)` : '100%'}
          >
            <Box height='100%' position={'relative'} pt={1} px={1} sx={FLEXIBLE}>
              <DisplayFormContent
                isDirty={isDirty}
                isSectionWindow={isSectionWindow}
                isValid={isValid}
                methods={methods}
              />
              {isFetchingForm && (
                <AppLoader
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  }}
                />
              )}
            </Box>
          </PageContentLayout>
        ) : (
          <DisplayFormContent isDirty={isDirty} methods={methods} />
        )}
      </FormStateContext.Provider>
    </FormContext.Provider>
  )
}
